/**
 * Per-symbol payout multipliers, indexed by match count.
 *
 * <p>cs-core's {@code PayoutConfiguration} carries this data plus a few
 * extras we'll need later (re-trigger handling, scatter wilds, free-spin
 * award counts). v0 ships the simple {symbolId → matchCount → multiplier}
 * shape because that's what 3x3 paylines need.
 *
 * <p>{@code multiplier(symbolId, matchCount)} returns the bet multiplier;
 * {@code winnings = bet * multiplier}. Returns 0 for sub-threshold matches
 * (typically <3) so callers can sum without conditionals.
 */
export interface PayoutTable {
  multiplier(symbolId: string, matchCount: number): number;
}

export type PayoutMap = Readonly<Record<string, Readonly<Record<number, number>>>>;

export class StaticPayoutTable implements PayoutTable {
  constructor(private readonly map: PayoutMap) {}

  multiplier(symbolId: string, matchCount: number): number {
    const bySymbol = this.map[symbolId];
    if (!bySymbol) {
      return 0;
    }
    return bySymbol[matchCount] ?? 0;
  }
}

/**
 * Default 5-reel payout multipliers — the MMS-shaped paytable.
 *
 * <p>Calibrated so a typical 10-coin spin returns 0 most of the time,
 * occasionally 20–50 (low-symbol 3-of-a-kind), rarely 100s (mid-symbol 4
 * or high-symbol 3), and very rarely 1000+ on a gem 5-of-a-kind. The
 * curve mirrors cs-core's MMS shape: low symbols pay frequently but
 * small; high symbols pay rarely but big.
 *
 * <p>Tunable via {@link StaticPayoutTable}; 3-reel slots can use
 * {@code DEFAULT_PAYOUT_MAP_3X3} which pays only on 3-of-a-kind.
 */
export const DEFAULT_PAYOUT_MAP: PayoutMap = {
  cherry: { 3: 2, 4: 10, 5: 50 },
  lemon: { 3: 2, 4: 10, 5: 50 },
  orange: { 3: 3, 4: 15, 5: 75 },
  bell: { 3: 5, 4: 25, 5: 100 },
  star: { 3: 10, 4: 50, 5: 200 }, // also serves as wild (substitutes for any other symbol)
  seven: { 3: 25, 4: 100, 5: 300 },
  gem: { 3: 50, 4: 150, 5: 500 },
  // Scatter pays anywhere on the grid (independent of paylines) and is
  // the canonical free-spin trigger. 3+ scatters = bonus + cash.
  scatter: { 3: 5, 4: 20, 5: 100 },
};

/** 3-reel payout map — pays only on 3-of-a-kind (the only match count
 * possible on a 3×3 grid). Kept for the 3-reel demo / tests. */
export const DEFAULT_PAYOUT_MAP_3X3: PayoutMap = {
  cherry: { 3: 2 },
  lemon: { 3: 2 },
  orange: { 3: 3 },
  bell: { 3: 5 },
  star: { 3: 10 },
  seven: { 3: 25 },
  gem: { 3: 50 },
};
